import React from 'react'
import { PropertiesOf } from '@pushly/models/lib/types'
import { DisplayAnimationConfig } from '@pushly/models/lib/structs/display-animation-configuration'
import { AnimationType } from '@pushly/models/lib/enums/app-messages/app-message-animation-type'
import { getEnumKeys } from '../../../_utils/antd'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { DisplayAnimationBehavior } from '@pushly/models/lib/structs/animation-behavior'
import {
    ANIMATION_DISPLAY_NAME_MAP,
    DEFAULT_IAM_ANIMATION_DURATION_MS,
    DEFAULT_IAM_ENTER_ANIMATIONS,
    DEFAULT_IAM_EXIT_ANIMATIONS,
} from '../constants'
import { AnimationEffect } from '../types'
import { Select } from 'antd'
import { AppMessageSettingRow } from './app-message-setting-row'

export function getAnimationTypeOptions(time: AnimationEffect) {
    return getEnumKeys(AnimationType, 'string').map((at) => ({
        value: AnimationType[at],
        label: ANIMATION_DISPLAY_NAME_MAP[time][AnimationType[at]],
    }))
}

// using an array does not guarantee order - initially all animations have FADE added, if only FADE present use as value
export function getAnimationValue(animations: AnimationType[] | undefined): undefined | AnimationType {
    if (!animations) return

    if (animations.length > 1) {
        return animations.filter((a) => a !== AnimationType.FADE)[0]
    }

    return animations[0]
}

export function AppMessageAnimationsConfigBuilder() {
    const [context, dispatch] = useAppMessageBuilderContext()
    const { variants, selectedVariantIdx } = context
    const value = variants[selectedVariantIdx]

    const behavior = value.schedules![0].behavior
    const displayAnimations = behavior.displayAnimations
    const { enter, exit } = displayAnimations

    const handleAnimationChange = (change: Partial<DisplayAnimationBehavior>) => {
        dispatch({
            type: 'patch',
            entity: 'behavior',
            data: behavior.clone({ displayAnimations: displayAnimations.clone(change) }),
        })
    }

    const handleAnimationTypeChange = (type: AnimationType, effect: AnimationEffect) => {
        let animations = exit.animations
        if (animations?.length && type === AnimationType.FADE) {
            animations = [AnimationType.FADE]
        } else {
            // all animations default to ending with FADE to ensure a smooth visual
            animations = [type, AnimationType.FADE]
        }

        const updateEffect = effect === AnimationEffect.Enter ? enter : exit
        handleAnimationChange({ [effect]: updateEffect.clone({ animations }) })
    }

    const handleEnabledChange = (effect: AnimationEffect, enabled: boolean) => {
        const config = effect === AnimationEffect.Enter ? enter : exit
        const change: Partial<PropertiesOf<DisplayAnimationConfig>> = { enabled }

        if (enabled) {
            if (!config.animations?.length) {
                change.animations =
                    effect === AnimationEffect.Enter ? DEFAULT_IAM_ENTER_ANIMATIONS : DEFAULT_IAM_EXIT_ANIMATIONS
            }
            change.durationMilliseconds = DEFAULT_IAM_ANIMATION_DURATION_MS
        } else {
            change.animations = undefined
            change.durationMilliseconds = undefined
        }

        handleAnimationChange({ [effect]: config.clone(change) })
    }

    return (
        <>
            <AppMessageSettingRow
                label="Animate entry"
                htmlFor="amb-animation-enter"
                toggle={{
                    checked: enter.enabled,
                    disabled: context.disabled,
                    onChange: (enabled) => handleEnabledChange(AnimationEffect.Enter, enabled),
                }}
                offText="Appears without animation"
            >
                <Select<AnimationType>
                    id="amb-animation-enter"
                    value={getAnimationValue(enter.animations!)}
                    options={getAnimationTypeOptions(AnimationEffect.Enter)}
                    onChange={(type) => handleAnimationTypeChange(type, AnimationEffect.Enter)}
                    disabled={context.disabled}
                    style={{ width: 200 }}
                />
            </AppMessageSettingRow>

            <AppMessageSettingRow
                label="Animate exit"
                htmlFor="amb-animation-exit"
                info="Exit animations are only shown when a user clicks a dismiss action, the dedicated close button (if enabled), or when the auto dismiss time is enabled and has expired."
                toggle={{
                    checked: exit.enabled,
                    disabled: context.disabled,
                    onChange: (enabled) => handleEnabledChange(AnimationEffect.Exit, enabled),
                }}
                offText="Disappears without animation"
            >
                <Select<AnimationType>
                    id="amb-animation-exit"
                    value={getAnimationValue(exit.animations!)}
                    options={getAnimationTypeOptions(AnimationEffect.Exit)}
                    onChange={(type) => handleAnimationTypeChange(type, AnimationEffect.Exit)}
                    disabled={context.disabled}
                    style={{ width: 200 }}
                />
            </AppMessageSettingRow>
        </>
    )
}
